/**
 * OverviewTab - Main product overview with metrics, business model, and details
 * Displays comprehensive product information in organized sections
 */

import { memo } from 'react';
import type { ProductProfile } from '../../../types';
import { MetricDisplay } from './MetricDisplay';
import { CapabilityBadge } from './CapabilityBadge';
import { CompetitorCard } from './CompetitorCard';
import { SentimentCard } from './SentimentCard';
import { revenueModelLabels, pricingPositionLabels, shareTrendLabels } from './constants';

interface OverviewTabProps {
  data: ProductProfile;
}

function OverviewTabComponent({ data }: OverviewTabProps) {
  return (
    <>
      {/* Key Metrics */}
      <section className="bg-gray-50 rounded-lg p-4">
        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-4">
          Key Metrics
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {data.estimated_revenue && (
            <MetricDisplay metric={data.estimated_revenue} label="Revenue" />
          )}
          {data.customer_count && <MetricDisplay metric={data.customer_count} label="Customers" />}
          {data.market_position?.market_share_estimate && (
            <MetricDisplay
              metric={data.market_position.market_share_estimate}
              label="Market Share"
            />
          )}
          {data.market_position?.tam_estimate && (
            <MetricDisplay metric={data.market_position.tam_estimate} label="TAM" />
          )}
        </div>
      </section>

      {/* Business Model */}
      <section>
        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-3">
          Business Model
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {data.revenue_model && (
            <div className="rounded-lg border border-gray-200 p-3">
              <div className="text-xs text-gray-500">Revenue Model</div>
              <div className="font-medium text-gray-900">
                {revenueModelLabels[data.revenue_model]}
              </div>
            </div>
          )}
          <div className="rounded-lg border border-gray-200 p-3">
            <div className="text-xs text-gray-500">Pricing Position</div>
            <div className="font-medium text-gray-900">
              {pricingPositionLabels[data.pricing_position]}
            </div>
          </div>
          {data.revenue_contribution !== undefined && data.revenue_contribution > 0 && (
            <div className="rounded-lg border border-gray-200 p-3">
              <div className="text-xs text-gray-500">Revenue Contribution</div>
              <div className="font-medium text-gray-900">
                {data.revenue_contribution.toFixed(1)}% of company
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Differentiation */}
      {data.differentiation_claim && (
        <section className="bg-primary-50 rounded-lg p-4">
          <h3 className="text-sm font-bold text-primary-900 uppercase tracking-wide mb-2">
            Differentiation
          </h3>
          <p className="text-gray-800">{data.differentiation_claim}</p>
        </section>
      )}

      {/* Competitors */}
      {data.primary_competitors.length > 0 && (
        <section>
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-3">
            Primary Competitors
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {data.primary_competitors.map((comp) => (
              <CompetitorCard key={comp.name} competitor={comp} />
            ))}
          </div>
        </section>
      )}

      {/* Market Position */}
      {data.market_position && (
        <section>
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-3">
            Market Position
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {data.market_position.market_segment && (
              <div className="rounded-lg border border-gray-200 p-3">
                <div className="text-xs text-gray-500">Market Segment</div>
                <div className="font-medium text-gray-900">
                  {data.market_position.market_segment}
                </div>
              </div>
            )}
            <div className="rounded-lg border border-gray-200 p-3">
              <div className="text-xs text-gray-500">Share Trend</div>
              <div
                className={`font-medium ${shareTrendLabels[data.market_position.share_trend].color}`}
              >
                {shareTrendLabels[data.market_position.share_trend].label}
              </div>
            </div>
            {data.market_position.geographic_focus.length > 0 && (
              <div className="rounded-lg border border-gray-200 p-3 col-span-2">
                <div className="text-xs text-gray-500">Geographic Focus</div>
                <div className="font-medium text-gray-900">
                  {data.market_position.geographic_focus.join(', ')}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* User Sentiment */}
      {data.sentiment.length > 0 && (
        <section>
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-3">
            User Sentiment
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.sentiment.map((s) => (
              <SentimentCard key={s.platform} sentiment={s} />
            ))}
          </div>
        </section>
      )}

      {/* Wins & Losses */}
      {(data.known_wins.length > 0 || data.known_losses.length > 0) && (
        <section className="grid grid-cols-2 gap-4">
          {data.known_wins.length > 0 && (
            <div className="rounded-lg border border-green-200 bg-green-50 p-4">
              <h3 className="text-sm font-bold text-green-900 uppercase tracking-wide mb-2">
                Known Wins
              </h3>
              <ul className="space-y-1">
                {data.known_wins.map((win) => (
                  <li key={win} className="text-sm text-green-800 flex items-start gap-2">
                    <span className="text-green-600">+</span>
                    {win}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {data.known_losses.length > 0 && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
              <h3 className="text-sm font-bold text-red-900 uppercase tracking-wide mb-2">
                Known Losses
              </h3>
              <ul className="space-y-1">
                {data.known_losses.map((loss) => (
                  <li key={loss} className="text-sm text-red-800 flex items-start gap-2">
                    <span className="text-red-600">-</span>
                    {loss}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      {/* Customers */}
      {(data.notable_customers.length > 0 || data.target_customers.length > 0) && (
        <section>
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-3">
            Customers
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.notable_customers.length > 0 && (
              <div className="rounded-lg border border-gray-200 p-3">
                <div className="text-xs text-gray-500 mb-2">Notable Customers</div>
                <div className="flex flex-wrap gap-2">
                  {data.notable_customers.map((customer) => (
                    <span
                      key={customer}
                      className="inline-flex items-center rounded bg-gray-100 px-2 py-1 text-sm text-gray-700"
                    >
                      {customer}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {data.target_customers.length > 0 && (
              <div className="rounded-lg border border-gray-200 p-3">
                <div className="text-xs text-gray-500 mb-2">Target Segments</div>
                <div className="flex flex-wrap gap-2">
                  {data.target_customers.map((segment) => (
                    <span
                      key={segment}
                      className="inline-flex items-center rounded bg-blue-50 px-2 py-1 text-sm text-blue-700"
                    >
                      {segment.replace(/_/g, ' ')}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Integrations */}
      {(data.key_integrations.length > 0 || data.integration_partners.length > 0) && (
        <section>
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-3">
            Integration Ecosystem
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.key_integrations.length > 0 && (
              <div className="rounded-lg border border-gray-200 p-3">
                <div className="text-xs text-gray-500 mb-2">Key Integrations</div>
                <div className="flex flex-wrap gap-2">
                  {data.key_integrations.map((integration) => (
                    <span
                      key={integration}
                      className="inline-flex items-center rounded bg-purple-50 px-2 py-1 text-sm text-purple-700"
                    >
                      {integration}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {data.integration_partners.length > 0 && (
              <div className="rounded-lg border border-gray-200 p-3">
                <div className="text-xs text-gray-500 mb-2">Integration Partners</div>
                <div className="flex flex-wrap gap-2">
                  {data.integration_partners.map((partner) => (
                    <span
                      key={partner}
                      className="inline-flex items-center rounded bg-teal-50 px-2 py-1 text-sm text-teal-700"
                    >
                      {partner}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Capabilities */}
      {data.capabilities.length > 0 && (
        <section>
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-3">
            Capabilities
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {data.capabilities.map((cap) => (
              <CapabilityBadge key={cap.name} capability={cap} />
            ))}
          </div>
        </section>
      )}

      {/* History / Acquisition */}
      {(data.acquisition_date || data.launch_date) && (
        <section className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-2">History</h3>
          <div className="flex flex-wrap gap-4 text-sm text-gray-600">
            {data.launch_date && (
              <div>
                <span className="text-gray-500">Launched:</span>{' '}
                {new Date(data.launch_date).toLocaleDateString()}
              </div>
            )}
            {data.acquisition_date && (
              <div>
                <span className="text-gray-500">Acquired:</span>{' '}
                {new Date(data.acquisition_date).toLocaleDateString()}
                {data.acquired_from && ` from ${data.acquired_from}`}
              </div>
            )}
          </div>
        </section>
      )}
    </>
  );
}

export const OverviewTab = memo(OverviewTabComponent);
